import { apiClient } from './apiClient';
import { API_ENDPOINTS } from '../config/api';

export interface LoginCredentials {
    username: string;
    password: string;
}

export interface LoginResponse {
    access_token: string;
    refresh_token: string;
    token_type: string;
    expires_in: number;
    user: {
        id: number;
        username: string;
        email: string;
        first_name: string;
        last_name: string;
        last_login: string;
    };
}

export interface RefreshResponse {
    access_token: string;
    refresh_token?: string;
    expires_in: number;
}

export interface ValidationResponse {
    valid: boolean;
    user?: {
        id: number;
        username: string;
        email: string;
    };
    error?: string;
}

class AuthService {
    private refreshPromise: Promise<RefreshResponse> | null = null;

    /**
     * 用户登录
     */
    async login(credentials: LoginCredentials): Promise<LoginResponse> {
        const response = await apiClient.post<LoginResponse>(
            API_ENDPOINTS.AUTH.LOGIN,
            credentials
        );

        if (response.error) {
            throw new Error(response.error);
        }

        if (!response.data) {
            throw new Error('登录响应数据为空');
        }

        return response.data;
    }

    /**
     * 用户登出
     */
    async logout(refreshToken?: string): Promise<void> {
        try {
            await apiClient.post(API_ENDPOINTS.AUTH.LOGOUT, {
                refresh_token: refreshToken
            });
        } catch (error) {
            // 登出失败不抛出错误，确保本地状态能够清除
            console.error('Logout error:', error);
        }
    }

    /**
     * 刷新访问令牌
     */
    async refreshToken(refreshToken: string): Promise<RefreshResponse> {
        // 防止并发刷新
        if (this.refreshPromise) {
            return this.refreshPromise;
        }

        this.refreshPromise = this._performRefresh(refreshToken);

        try {
            const result = await this.refreshPromise;
            return result;
        } finally {
            this.refreshPromise = null;
        }
    }

    private async _performRefresh(refreshToken: string): Promise<RefreshResponse> {
        const response = await apiClient.post<any>(
            API_ENDPOINTS.AUTH.REFRESH,
            { refresh: refreshToken }
        );

        if (response.error) {
            throw new Error(response.error);
        }

        if (!response.data) {
            throw new Error('刷新令牌响应数据为空');
        }

        // ✅ 字段规范化：simplejwt TokenRefreshView 返回 {access, refresh?}，
        // 不包含 expires_in——从 JWT payload 解码过期时间
        const data = response.data;
        const accessToken: string | undefined = data.access ?? data.access_token;
        if (!accessToken) {
            throw new Error('刷新响应中缺少 access token');
        }

        let expiresIn = 3600;
        const expiry = this.getTokenExpiry(accessToken);
        if (expiry) {
            expiresIn = Math.max(0, Math.floor((expiry - Date.now()) / 1000));
        }

        return {
            access_token: accessToken,
            refresh_token: data.refresh ?? data.refresh_token,
            expires_in: expiresIn,
        };
    }

    /**
     * 验证令牌有效性
     */
    async validateToken(accessToken: string): Promise<ValidationResponse> {
        const response = await apiClient.get<ValidationResponse>(
            API_ENDPOINTS.AUTH.VALIDATE,
            `Bearer ${accessToken}`
        );

        if (response.error) {
            return { valid: false, error: response.error };
        }

        return response.data || { valid: false, error: '验证响应为空' };
    }

    /**
     * 获取用户资料
     */
    async getUserProfile(accessToken: string) {
        const response = await apiClient.get(
            API_ENDPOINTS.AUTH.PROFILE,
            `Bearer ${accessToken}`
        );

        if (response.error) {
            throw new Error(response.error);
        }

        return response.data;
    }

    /**
     * 修改密码（需登录）
     */
    async changePassword(oldPassword: string, newPassword: string, accessToken: string): Promise<void> {
        const response = await apiClient.post<{ message?: string }>(
            API_ENDPOINTS.AUTH.CHANGE_PASSWORD,
            { old_password: oldPassword, new_password: newPassword },
            `Bearer ${accessToken}`
        );

        if (response.error) {
            throw new Error(response.error);
        }
    }

    /**
     * 找回密码 - 第一步：申请验证码
     * 返回开发环境下调试用的 dev_code（生产环境不存在）
     */
    async requestPasswordReset(username: string): Promise<{ message: string; dev_code?: string }> {
        const response = await apiClient.post<{ message: string; dev_code?: string }>(
            API_ENDPOINTS.AUTH.PASSWORD_RESET_REQUEST,
            { username }
        );

        if (response.error) {
            throw new Error(response.error);
        }

        return response.data || { message: '如果账号存在，重置验证码已发送' };
    }

    /**
     * 找回密码 - 第二步：验证码 + 新密码完成重置
     */
    async confirmPasswordReset(username: string, code: string, newPassword: string): Promise<void> {
        const response = await apiClient.post<{ message?: string }>(
            API_ENDPOINTS.AUTH.PASSWORD_RESET_CONFIRM,
            { username, code, new_password: newPassword }
        );

        if (response.error) {
            throw new Error(response.error);
        }
    }

    /**
     * 检查令牌是否过期（基于时间戳）
     */
    isTokenExpired(tokenExpiry: number): boolean {
        if (!tokenExpiry) return true;
        // 提前5分钟刷新
        return Date.now() > (tokenExpiry - 5 * 60 * 1000);
    }

    /**
     * 生成Basic Auth字符串（向后兼容）
     */
    generateBasicAuth(username: string, password: string): string {
        return btoa(`${username}:${password}`);
    }

    /**
     * 从JWT Token中解析用户信息（不验证签名）
     */
    parseJwtPayload(token: string): any {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split('')
                    .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                    .join('')
            );
            return JSON.parse(jsonPayload);
        } catch (error) {
            console.error('Failed to parse JWT payload:', error);
            return null;
        }
    }

    /**
     * 获取Token过期时间
     */
    getTokenExpiry(token: string): number | null {
        const payload = this.parseJwtPayload(token);
        return payload?.exp ? payload.exp * 1000 : null;
    }
}

export const authService = new AuthService();